/* eslint-disable unicorn/filename-case */
import { useNavigate } from '@tanstack/react-router';
import {
  Announcement03,
  Building06,
  Check,
  File04,
  Grid01,
  LogOut01,
  PresentationChart02,
  Settings02,
  Tag01,
} from '@untitled-ui/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppSidebar, SidebarNavSection } from '../../components/app-sidebar';
import { Avatar } from '../../components/avatar';
import { Badge } from '../../components/badge';
import { BrandsNav } from '../../components/brands-nav';
import {
  AppDropdownBody,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
} from '../../components/dropdown-menu';
import { AnimatedSettingsIcon } from '../../components/icons/animated-settings-icon';
import { DropdownMenuOption, MenuOption, MenuOptionIcon } from '../../components/menu-option';
import { ModalSignOut } from '../../components/modal-sign-out';
import { NavItem } from '../../components/sidebar-nav-item';
import { getConfig } from '../../config';
import { type Brand } from '../../entity/api/brand';
import { type Organization } from '../../entity/api/organization';
import type { OrganizationBilling } from '../../entity/api/organization-billing';
import { type OrganizationUser, OrganizationUserRole } from '../../entity/api/organization-user';
import { type User } from '../../entity/api/user';
import { trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import { getQueryClient } from '../../sdk/request';
import { getAuthUserFromCache, getStores, useOrganizationUsersList } from '../../services/storage';
import { UIKey } from '../../services/storage/ui-key-value';
import { tGlobal, tNamespace } from '../../services/translation';
import { Feature, isFeatureAvailable } from '../../shared/feature';
import { switchToOrganization } from '../../shared/organization-switcher';
import { returnFirstLetter } from '../../shared/string';
import { getUserDefaults, layoutUserName } from '../../shared/user';

const t = tNamespace('app.sidebar');

function handleLogout() {
  getStores()
    .users.getState()
    .signOut()
    .catch(function (err) {
      captureException(
        err,
        {
          file: 'app/-sidebar.tsx',
          method: 'handleLogout',
          infoMessage: 'unable to logout',
        },
        trackError
      );
    });
}

type UserSettingsNavProps = {
  user?: {
    firstName: User['firstName'];
    lastName: User['lastName'];
    email: User['email'];
  };
  organizations?: {
    _id: Organization['_id'];
    name: Organization['name'];
  }[];
  orgUsers?: {
    organizationId: OrganizationUser['organizationId'];
    role: OrganizationUser['role'];
  }[];
  currentOrganization: Organization | null | undefined;
  brands: Brand[] | undefined;
};

function UserSettingsNav(props: UserSettingsNavProps) {
  const { user, organizations, orgUsers, currentOrganization } = props;
  const navigate = useNavigate();
  const [switchingOrganization, setSwitchingOrganization] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openModalSignOut, setOpenModalSignOut] = useState(false);

  const [isOwner, setIsOwner] = useState(false);

  const handleModalSignOutChange = useCallback(function (open: boolean) {
    setOpenModalSignOut(open);
  }, []);

  // TanStack Query hook for auth user
  const queryClient = getQueryClient();
  const currentUser = getAuthUserFromCache(queryClient);

  // TanStack Query for org users by org ID
  const { data: organizationUsersData } = useOrganizationUsersList(currentOrganization?._id);
  const organizationUsers = useMemo(() => organizationUsersData ?? [], [organizationUsersData]);

  useEffect(
    function () {
      if (organizationUsers.length === 0 || currentUser === null || currentUser === undefined) {
        return;
      }
      const currentUserOrg = organizationUsers.find(
        (orgUser) => orgUser?.userId === currentUser._id
      );
      if (currentUserOrg == null) {
        return;
      }
      setIsOwner(
        currentUserOrg?.role === OrganizationUserRole.Owner ||
          currentUserOrg?.role === OrganizationUserRole.SuperAdmin
      );
    },
    [currentUser, organizationUsers]
  );

  const settingsLink = isOwner
    ? `/app/settings/organization/${currentOrganization?._id}/plan`
    : `/app/settings/organization/${currentOrganization?._id}/users`;

  if (user == null || organizations == null || orgUsers == null) {
    return <NavItem isLoading={true} label={''} />;
  }

  return (
    <>
      <ModalSignOut
        open={openModalSignOut}
        onOpenChange={handleModalSignOutChange}
        title={tGlobal('confirmSignOut')}
        onSignOut={handleLogout}
      />
      <DropdownMenu
        position="start"
        side="top"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        trigger={
          <MenuOption
            mainText={layoutUserName(user, true)}
            className="group/settings-trigger max-h-[36px] w-full"
            leftElement={
              <Avatar
                size="sm"
                color="accent-contrast"
                initials={`${returnFirstLetter(getUserDefaults(user).firstName)}`}
              />
            }
            rightElement={
              <MenuOptionIcon variant="gray">
                <AnimatedSettingsIcon
                  width={16}
                  height={16}
                  groupName="group/settings-trigger"
                  bgColor="#F3F5FB"
                />
              </MenuOptionIcon>
            }
          />
        }
      >
        <AppDropdownBody>
          <DropdownMenuOption
            mainText={layoutUserName(user, true)}
            secondaryText={`${user.email}`}
            className="hover:bg-branding-gray-100"
            leftElement={
              <Avatar
                size="md"
                color="accent"
                initials={`${returnFirstLetter(getUserDefaults(user).firstName)}`}
              />
            }
            onClick={function () {
              navigate({ to: '/app/settings/user' }).catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'app/-sidebar.tsx',
                    method: 'navigate',
                    infoMessage: 'unable to navigate to /app/settings/user',
                  },
                  trackError
                );
              });
            }}
          />
          <DropdownSeparator />
          <DropdownLabel>{t('organizations')}</DropdownLabel>
          {organizations.map(function (organization) {
            if (currentOrganization == null) {
              return null;
            }
            const isCurrentOrganization = currentOrganization._id === organization._id;
            return (
              <NavItem
                key={organization._id}
                label={organization.name}
                icon={<Building06 width={16} height={16} />}
                secondaryIcon={isCurrentOrganization ? <Check width={16} height={16} /> : null}
                isActive={isCurrentOrganization}
                isLoading={switchingOrganization === organization._id}
                onClick={async function () {
                  if (switchingOrganization != null || isCurrentOrganization) {
                    // Prevent multiple simultaneous switches
                    return;
                  }

                  setSwitchingOrganization(organization._id);

                  try {
                    const result = await switchToOrganization(organization._id);

                    if (!result.success) {
                      captureException(
                        new Error(result.error ?? 'Failed to switch organization'),
                        {
                          file: 'app/-sidebar.tsx',
                          method: 'switchToOrganization',
                          infoMessage: 'unable to switch to selected organization',
                          organizationId: organization._id,
                          organizationName: organization.name,
                        },
                        trackError
                      );
                    }
                  } finally {
                    setSwitchingOrganization(null);
                  }
                }}
              />
            );
          })}
          <DropdownSeparator />
          {currentOrganization == null ? null : (
            <NavItem
              label={t('settings')}
              icon={<Settings02 width={16} height={16} />}
              href={settingsLink}
              params={
                currentOrganization?._id != null ? { id: currentOrganization._id } : undefined
              }
            />
          )}
          <NavItem
            label={tGlobal('logout')}
            icon={<LogOut01 width={16} height={16} className="text-red-700" />}
            onClick={function () {
              setDropdownOpen(false);
              setOpenModalSignOut(true);
            }}
            variant="red"
          />
        </AppDropdownBody>
      </DropdownMenu>
    </>
  );
}

type SidebarProps = {
  user: UserSettingsNavProps['user'];
  organizations: Organization[] | undefined;
  currentOrganization: Organization | undefined;
  orgUsers: OrganizationUser[] | undefined;
  currentOrgBilling: OrganizationBilling | undefined;
  brands: Brand[] | undefined;
  currentBrand: Brand | undefined;
  currentPath: string;
  variant?: 'desktop' | 'mobile';
};

export function Sidebar(props: SidebarProps) {
  const {
    user,
    organizations,
    currentOrganization,
    orgUsers,
    brands,
    currentPath,
    currentBrand,
    currentOrgBilling,
  } = props;

  const isOrgLoading = getStores().uiKeyValue(function (state) {
    return state.getFromStore(UIKey.IS_ORG_LOADING) ?? false;
  });

  const filteredBrands = useMemo(
    function () {
      return (brands ?? []).filter(function (brand) {
        return brand.organizationId === currentOrganization?._id;
      });
    },
    [brands, currentOrganization]
  );

  if (currentOrganization == null || currentBrand == null) {
    return (
      <div className="grid h-full min-w-dashboardSidebar max-w-dashboardSidebar auto-rows-min grid-cols-1 grid-rows-[auto_auto_1fr_auto_auto] gap-6 bg-neutral-50 px-2 py-6"></div>
    );
  }

  return (
    <AppSidebar
      currentPath={currentPath}
      currentOrganization={currentOrganization}
      currentOrgBilling={currentOrgBilling}
      currentBrand={currentBrand}
      variant={props.variant ?? 'desktop'}
      navigator={
        <BrandsNav
          brands={filteredBrands}
          currentBrand={currentBrand}
          currentOrganization={currentOrganization}
          isDisabled={isOrgLoading}
        />
      }
      navSections={[
        ...(isFeatureAvailable(Feature.AUDIT)
          ? [
              <SidebarNavSection title={t('analyse')} key="analyse">
                <NavItem
                  label={t('seoStrategy')}
                  icon={<PresentationChart02 width={16} height={16} />}
                  href={`/app/seo-strategy/${currentBrand._id}`}
                  isActive={currentPath.startsWith('/app/seo-strategy')}
                />
              </SidebarNavSection>,
            ]
          : []),
        <SidebarNavSection title={t('generate')} key="content">
          {getConfig().product.enabledBrandTypes.includes(currentBrand.productType ?? '') ? (
            <>
              <NavItem
                label={t('products')}
                icon={<Tag01 width={16} height={16} />}
                href="/app/products"
                isActive={currentPath.startsWith('/app/products')}
                isDisabled={!isFeatureAvailable(Feature.PRODUCT)}
                badge={
                  !isFeatureAvailable(Feature.PRODUCT) ? (
                    <Badge size="sm" color="yellow">
                      {t('comingSoon')}
                    </Badge>
                  ) : null
                }
              />
              <NavItem
                label={t('collections')}
                icon={<Grid01 width={16} height={16} />}
                href="/app/brand-context"
                search={{ brandId: currentBrand._id }}
                isActive={currentPath === '/app/collections'}
                badge={
                  <Badge size="sm" color="yellow">
                    {t('comingSoon')}
                  </Badge>
                }
                isDisabled
              />
            </>
          ) : null}
          <NavItem
            label={t('blogPosts')}
            icon={<File04 width={16} height={16} />}
            href="/app/blogs"
            isActive={currentPath === '/app/blogs'}
          />
        </SidebarNavSection>,
        <SidebarNavSection title={t('configuration')} key="configuration" className="mb-6">
          <NavItem
            label={t('brandVoice')}
            icon={<Announcement03 width={16} height={16} />}
            href="/app/brand-context"
            search={{ brandId: currentBrand._id }}
            isActive={currentPath === '/app/brand-context'}
          />
        </SidebarNavSection>,
      ]}
      footer={
        <UserSettingsNav
          user={user}
          organizations={organizations}
          currentOrganization={currentOrganization}
          orgUsers={orgUsers}
          brands={brands}
        />
      }
    />
  );
}
